/**
 * @file: listTheme.ts
 *
 * Centralized color palette for the inbox — the mobile page wrapper,
 * the chat/feed thread rows, and the wrapping list panel all read from
 * here so the surfaces stay visually aligned. Theme is layout-aware:
 * the existing WeChat-style tiered grays apply to the desktop master-
 * detail (where the rows live in a tinted panel column), while mobile
 * goes plain white to feel native to the phone surface. Per-tier
 * dividers + text colors are shared across layouts so the row hierarchy
 * still reads.
 *
 * Add a new layout variant by extending `InboxTheme` here and branching
 * on it in `useInboxTheme`. Consumers shouldn't hard-code colors —
 * read them off the theme object.
 */

import { useIsDesktopLayout } from '@euler/functions/responsiveness';

export interface InboxTheme {
  /** Page wrapper background (the mobile home surface; the header +
   * tab-bar inherit from here since they're transparent). */
  page: string;
  /** Background of the list panel that wraps the FlatList. */
  panel: string;
  /** Per-tier row background. */
  row: {
    /** Active in the desktop master-detail. */
    selected: string;
    /** Scope-pinned essentials (feed: maintenance-plans / rewards). */
    defaultPinned: string;
    /** System + user-pinned threads (feed) or any pinned thread (chat). */
    otherPinned: string;
    /** Regular unpinned threads. */
    unpinned: string;
  };
  /** Per-tier bottom hairline color. */
  divider: {
    /** Edge-to-edge boundary between the pinned and unpinned sections. */
    section: string;
    defaultPinned: string;
    otherPinned: string;
    unpinned: string;
  };
  text: {
    title: string;
    titleSelected: string;
    meta: string;
    metaSelected: string;
    time: string;
    timeSelected: string;
    previewUnread: string;
    previewRead: string;
    previewSelected: string;
    mutedIcon: string;
    mutedIconSelected: string;
  };
}

/** Back-compat alias for the old name; kept until callers migrate. */
export type InboxListTheme = InboxTheme;

const sharedText: InboxListTheme['text'] = {
  title: '#111',
  titleSelected: '#ffffff',
  meta: '#888',
  metaSelected: 'rgba(255,255,255,0.8)',
  time: '#9ca3af',
  timeSelected: 'rgba(255,255,255,0.8)',
  previewUnread: '#374151',
  previewRead: '#9ca3af',
  previewSelected: 'rgba(255,255,255,0.85)',
  mutedIcon: '#9ca3af',
  mutedIconSelected: 'rgba(255,255,255,0.85)',
};

/** Desktop master-detail palette — WeChat-style tinted grays. */
const desktopTheme: InboxTheme = {
  page: '#eeeef0',
  panel: '#eeeef0',
  row: {
    selected: '#3b82f6',
    defaultPinned: '#e3e3e5',
    otherPinned: '#e8e8ea',
    unpinned: '#eeeef0',
  },
  divider: {
    section: '#cccccf',
    defaultPinned: '#d0d0d2',
    otherPinned: '#d4d4d6',
    unpinned: '#d8d8da',
  },
  text: sharedText,
};

/** Mobile palette — plain white page + panel + unpinned rows; pinned
 * rows pick up the desktop's unpinned gray (`#eeeef0`) so they stand
 * out against the white surface without the heavier tiered grays the
 * desktop master-detail uses. Dividers still carry the tier hierarchy.
 */
const mobileTheme: InboxTheme = {
  ...desktopTheme,
  page: '#ffffff',
  panel: '#ffffff',
  row: {
    selected: '#3b82f6',
    defaultPinned: '#f3f3f5',
    otherPinned: '#f3f3f5',
    unpinned: '#ffffff',
  },
};

export function useInboxTheme(): InboxTheme {
  const isDesktop = useIsDesktopLayout();
  return isDesktop ? desktopTheme : mobileTheme;
}

/** Back-compat alias for the old name; kept until callers migrate. */
export const useInboxListTheme = useInboxTheme;
